/**
 * GovCostAnalysisTab — AI-assisted cost analysis for a solicitation.
 *
 * Mounted inside GovSolicitationDetailPage as the 8th tab.
 * Handles: container fetch, version list, current version rendering,
 * generate/refresh runs, live progress polling, editing, finalization.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import { S, GOV_COLORS, fmtMoney, fmtMoneyFull, statusMeta, confidenceColor, confidenceLabel } from './govShared';
import GovCostAnalysisItemsTable from './GovCostAnalysisItemsTable';
import GovCostAnalysisSidebar from './GovCostAnalysisSidebar';
import GovCostAnalysisRisksAssumptions from './GovCostAnalysisRisksAssumptions';
import GovCostAnalysisExportModal from './GovCostAnalysisExportModal';
import GovCostAnalysisActualsPanel from './GovCostAnalysisActualsPanel';
import GovUploadedCostAnalyses from './GovUploadedCostAnalyses';

interface Props {
  solicitationId: string;
  isConstruction?: boolean;      // If true, show actuals import panel
  constructionJobId?: string;
  onDocumentsChanged?: () => void | Promise<void>;
}

interface VersionSummary {
  id: string;
  version_number: number;
  name?: string;
  status: string;
  total_low: number;
  total_expected: number;
  total_high: number;
  recommended_bid: number;
  confidence_score?: number;
  is_stale: boolean;
  stale_reason?: string;
  created_at: string;
  finalized_at?: string;
  ai_model?: string;
  calculation_version: string;
}

interface FullVersion extends VersionSummary {
  contingency_pct: number;
  op_pct: number;
  bond_pct: number;
  risk_buffer_fixed: number;
  strategic_adjustment_fixed: number;
  rounding_increment: number;
  bid_strategy: string;
  win_probability_pct?: number;
  competitor_notes?: string;
  executive_summary?: string;
  direct_low: number;
  direct_expected: number;
  direct_high: number;
  sensitivity_json?: any;
  approver_name_stamp?: string;
  source_snapshot_id?: string;
}

interface RunStatus {
  id: string;
  run_type: string;
  status: string;
  current_stage?: string;
  progress_pct: number;
  ai_input_tokens?: number;
  ai_output_tokens?: number;
  ai_cost_estimate_cents?: number;
  error_message?: string;
}

export default function GovCostAnalysisTab({ solicitationId, isConstruction, constructionJobId, onDocumentsChanged }: Props) {
  const [analysis, setAnalysis] = useState<any>(null);
  const [versions, setVersions] = useState<VersionSummary[]>([]);
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);
  const [fullVersion, setFullVersion] = useState<FullVersion | null>(null);
  const [items, setItems] = useState<any[]>([]);
  const [sources, setSources] = useState<any[]>([]);
  const [itemSources, setItemSources] = useState<any[]>([]);
  const [risks, setRisks] = useState<any[]>([]);
  const [assumptions, setAssumptions] = useState<any[]>([]);
  const [latestRun, setLatestRun] = useState<RunStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState<any[]>([]);
  const [showRunModal, setShowRunModal] = useState(false);
  const [selectedDocIds, setSelectedDocIds] = useState<string[]>([]);
  const [queueing, setQueueing] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [finalizingVersion, setFinalizingVersion] = useState(false);

  const fetchContainer = useCallback(async () => {
    try {
      const { data } = await api.get(`/gov/solicitations/${solicitationId}/cost-analysis`);
      setAnalysis(data?.analysis || null);
      setVersions(data?.versions || []);
      const latest = data?.versions?.[0];
      if (latest && !selectedVersionId) setSelectedVersionId(latest.id);
    } catch (e) { console.error(e); }
  }, [solicitationId, selectedVersionId]);

  const fetchVersion = useCallback(async (vid: string) => {
    try {
      const { data } = await api.get(`/gov/cost-analysis-versions/${vid}`);
      setFullVersion(data?.version || null);
      setItems(data?.items || []);
      setSources(data?.sources || []);
      setItemSources(data?.item_sources || []);
      setRisks(data?.risks || []);
      setAssumptions(data?.assumptions || []);
      setLatestRun(data?.latest_run || null);
    } catch (e) { console.error(e); }
  }, []);

  const fetchDocuments = useCallback(async () => {
    try {
      const { data } = await api.get(`/gov/solicitations/${solicitationId}/documents`);
      const docs = data?.documents || [];
      setDocuments(docs);
      // Default: select everything the backend can read — natively supported
      // formats PLUS anything we can convert (xlsx, docx, csv, etc.)
      const nativeExts = ['.pdf', '.txt', '.md', '.csv', '.html', '.json', '.xml'];
      const nativeMimes = ['application/pdf', 'text/plain', 'text/markdown', 'text/csv', 'text/html', 'application/json', 'application/xml'];
      const convertibleExts = ['.xlsx', '.xls', '.xlsm', '.docx', '.tsv'];
      const readable = docs.filter((d: any) => {
        const name = (d.file_name || d.name || '').toLowerCase();
        const mime = (d.mime_type || '').toLowerCase();
        return nativeMimes.includes(mime)
          || nativeExts.some(ext => name.endsWith(ext))
          || convertibleExts.some(ext => name.endsWith(ext));
      });
      setSelectedDocIds(readable.map((d: any) => d.id));
    } catch { setDocuments([]); }
  }, [solicitationId]);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchContainer(), fetchDocuments()]).finally(() => setLoading(false));
  }, [fetchContainer, fetchDocuments]);

  useEffect(() => {
    if (selectedVersionId) fetchVersion(selectedVersionId);
  }, [selectedVersionId, fetchVersion]);

  // Poll for run progress when processing
  useEffect(() => {
    if (!latestRun) return;
    if (latestRun.status !== 'queued' && latestRun.status !== 'running') return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/gov/cost-analysis-runs/${latestRun.id}`);
        const r = data?.run;
        if (!r) return;
        setLatestRun(r);
        if (r.status === 'completed' || r.status === 'failed' || r.status === 'canceled') {
          clearInterval(t);
          if (selectedVersionId) fetchVersion(selectedVersionId);
          fetchContainer();
        }
      } catch (e) { console.error(e); }
    }, 3000);
    return () => clearInterval(t);
  }, [latestRun, selectedVersionId, fetchVersion, fetchContainer]);

  const queueRun = async (runType: 'generate' | 'refresh') => {
    if (!analysis) return;
    setQueueing(true);
    try {
      const body: any = { run_type: runType, selected_document_ids: selectedDocIds };
      if (runType === 'refresh' && selectedVersionId && fullVersion?.status !== 'final') {
        // Reuse current draft version to preserve any manual edits
        body.version_id = selectedVersionId;
      }
      const { data } = await api.post(`/gov/cost-analyses/${analysis.id}/runs`, body);
      setShowRunModal(false);
      // Switch to whatever version the run is attached to
      if (data?.version_id) setSelectedVersionId(data.version_id);
      await fetchContainer();
    } catch (e: any) {
      alert(e?.response?.data?.error || e?.message || 'Failed to queue run');
    } finally { setQueueing(false); }
  };

  const finalizeVersion = async () => {
    if (!selectedVersionId) return;
    if (!window.confirm('Finalize this version? It will become immutable and stamped with your name as approver.')) return;
    setFinalizingVersion(true);
    try {
      await api.post(`/gov/cost-analysis-versions/${selectedVersionId}/finalize`, {});
      await Promise.all([fetchContainer(), fetchVersion(selectedVersionId)]);
    } catch (e: any) {
      alert(e?.response?.data?.error || 'Failed to finalize');
    } finally { setFinalizingVersion(false); }
  };

  const cloneVersion = async () => {
    if (!analysis || !selectedVersionId) return;
    const name = window.prompt('Name for the new version:', `Revision of ${fullVersion?.name || 'v' + fullVersion?.version_number}`);
    if (name === null) return;
    try {
      const { data } = await api.post(`/gov/cost-analyses/${analysis.id}/versions`, {
        name: name || undefined,
        clone_from_version_id: selectedVersionId,
      });
      const newVid = data?.version?.id;
      await fetchContainer();
      if (newVid) setSelectedVersionId(newVid);
    } catch (e: any) {
      alert(e?.response?.data?.error || 'Failed to create new version');
    }
  };

  const deleteVersion = async () => {
    if (!selectedVersionId || !fullVersion) return;
    if (fullVersion.status === 'final') { alert('Cannot delete a finalized version.'); return; }
    if (!window.confirm(`Delete ${fullVersion.name || 'v' + fullVersion.version_number}? This cannot be undone.`)) return;
    try {
      await api.delete(`/gov/cost-analysis-versions/${selectedVersionId}`);
      setSelectedVersionId(null);
      await fetchContainer();
    } catch (e: any) {
      alert(e?.response?.data?.error || 'Failed to delete');
    }
  };

  const runInProgress = latestRun && (latestRun.status === 'queued' || latestRun.status === 'running');
  const isFinal = fullVersion?.status === 'final';
  const isStale = fullVersion?.is_stale;

  const uploads = <GovUploadedCostAnalyses solicitationId={solicitationId} onChanged={async () => { await fetchDocuments(); await onDocumentsChanged?.(); }} />;
  if (loading) return <>{uploads}<div style={S.loading}>Loading cost analysis…</div></>;
  if (!analysis) return <>{uploads}<div style={S.empty}>Unable to load the estimate editor. Uploaded files remain available above.</div></>;

  // Empty state — no versions yet
  if (versions.length === 0) {
    return (
      <div style={{ padding: '0 4px' }}>
        {uploads}
        <div style={{ padding: 24, textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>💰</div>
        <h2 style={{ ...S.title, marginBottom: 8 }}>Create an AI-assisted estimate</h2>
        <p style={{ ...S.subtitle, marginBottom: 24 }}>
          Generate an AI-assisted cost estimate from the attached solicitation documents.
          <br />Claude will extract scope, propose line items with sources, and flag risks.
          <br />You edit anything; the app calculates the totals.
        </p>
        <button onClick={() => setShowRunModal(true)} style={{ ...S.addBtn, fontSize: 15, padding: '12px 24px' }}
          disabled={documents.length === 0}>
          {documents.length === 0 ? '⚠️ Attach documents first' : '🤖 Generate Cost Analysis'}
        </button>
        {showRunModal && renderRunModal()}
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '0 4px' }}>
      {uploads}
      {/* Stale banner */}
      {isStale && (
        <div style={{ ...S.card, borderLeft: `4px solid ${GOV_COLORS.warning}`, marginBottom: 16, background: '#3d2b0f' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: GOV_COLORS.warning }}>⚠️ SAM.gov amendment detected — this estimate is stale</div>
              <div style={{ color: GOV_COLORS.textMuted, fontSize: 12, marginTop: 4 }}>{fullVersion?.stale_reason || 'Source documents changed since this estimate was generated'}</div>
            </div>
            <button onClick={() => setShowRunModal(true)} style={S.addBtn}>🔄 Regenerate</button>
          </div>
        </div>
      )}

      {/* Version bar */}
      <div style={{ ...S.card, marginBottom: 16, padding: '12px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: GOV_COLORS.textMuted, letterSpacing: 0.5, textTransform: 'uppercase' }}>Version</span>
          <select value={selectedVersionId || ''} onChange={e => setSelectedVersionId(e.target.value)} style={{ ...S.select, minWidth: 260 }}>
            {versions.map(v => {
              const meta = statusMeta(v.status);
              return (
                <option key={v.id} value={v.id}>
                  {meta.icon} v{v.version_number} — {v.name || 'Untitled'} · {meta.label}
                </option>
              );
            })}
          </select>
          <div style={{ flex: 1 }} />
          {!isFinal && !runInProgress && (
            <>
              <button onClick={() => setShowRunModal(true)} style={S.addBtn}>🤖 {versions.length > 0 ? 'Regenerate' : 'Generate'}</button>
              <button onClick={finalizeVersion} disabled={finalizingVersion} style={{ ...S.addBtn, background: GOV_COLORS.success }}>
                {finalizingVersion ? 'Finalizing…' : '✅ Finalize'}
              </button>
            </>
          )}
          {isFinal && (
            <button onClick={cloneVersion} style={S.addBtn}>📄 New Version from This</button>
          )}
          <button onClick={() => setShowExport(true)} style={S.cancelBtn}>📤 Export</button>
          {!isFinal && <button onClick={deleteVersion} style={S.dangerBtn}>Delete</button>}
        </div>
        {isFinal && fullVersion?.approver_name_stamp && (
          <div style={{ marginTop: 8, color: GOV_COLORS.textMuted, fontSize: 12 }}>
            🔒 Finalized {new Date(fullVersion.finalized_at!).toLocaleString()} by {fullVersion.approver_name_stamp}
          </div>
        )}
      </div>

      {/* Run progress banner */}
      {runInProgress && (
        <div style={{ ...S.card, marginBottom: 16, borderLeft: `4px solid ${GOV_COLORS.accent}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ fontSize: 20 }}>🤖</div>
            <div style={{ flex: 1 }}>
              <div style={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>
                AI Analysis in progress — {latestRun?.current_stage || latestRun?.status}
                {latestRun?.progress_pct != null && ` · ${latestRun.progress_pct}%`}
              </div>
              <div style={{ marginTop: 6, height: 6, background: '#0F1923', borderRadius: 3, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${latestRun?.progress_pct || 0}%`, background: GOV_COLORS.accent, transition: 'width 0.5s' }} />
              </div>
              {latestRun?.ai_cost_estimate_cents != null && latestRun.ai_cost_estimate_cents > 0 && (
                <div style={{ color: GOV_COLORS.textMuted, fontSize: 11, marginTop: 6 }}>
                  Tokens: {latestRun.ai_input_tokens || 0} in / {latestRun.ai_output_tokens || 0} out · Cost: ${(latestRun.ai_cost_estimate_cents / 100).toFixed(2)}
                </div>
              )}
            </div>
            <button onClick={async () => {
              if (!window.confirm('Cancel this run?')) return;
              await api.post(`/gov/cost-analysis-runs/${latestRun!.id}/cancel`, {});
              fetchVersion(selectedVersionId!);
            }} style={S.cancelBtn}>Cancel</button>
          </div>
        </div>
      )}

      {/* Failed run banner */}
      {latestRun?.status === 'failed' && (
        <div style={{ ...S.card, marginBottom: 16, borderLeft: `4px solid ${GOV_COLORS.danger}` }}>
          <div style={{ color: GOV_COLORS.danger, fontWeight: 700, fontSize: 14 }}>❌ Run failed</div>
          <div style={{ color: GOV_COLORS.textMuted, fontSize: 12, marginTop: 4 }}>{latestRun.error_message}</div>
        </div>
      )}

      {fullVersion && (
        <>
          {/* Top-line KPIs */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 }}>
            <KPI label="Low" value={fmtMoney(fullVersion.total_low)} sub={`Direct: ${fmtMoney(fullVersion.direct_low)}`} color={GOV_COLORS.success} />
            <KPI label="Expected" value={fmtMoney(fullVersion.total_expected)} sub={`Direct: ${fmtMoney(fullVersion.direct_expected)}`} color={GOV_COLORS.accent} />
            <KPI label="High" value={fmtMoney(fullVersion.total_high)} sub={`Direct: ${fmtMoney(fullVersion.direct_high)}`} color={GOV_COLORS.warning} />
            <KPI label="Recommended Bid"
              value={fmtMoney(fullVersion.recommended_bid)}
              sub={fullVersion.confidence_score != null ? `Confidence: ${confidenceLabel(fullVersion.confidence_score)} (${Math.round(fullVersion.confidence_score)}%)` : 'Confidence: pending'}
              color={confidenceColor(fullVersion.confidence_score)} />
          </div>

          {/* Executive summary */}
          {fullVersion.executive_summary && (
            <div style={{ ...S.card, marginBottom: 16 }}>
              <div style={S.kvLabel}>Executive Summary</div>
              <div style={{ color: GOV_COLORS.text, fontSize: 13, lineHeight: 1.6, marginTop: 6, whiteSpace: 'pre-wrap' }}>{fullVersion.executive_summary}</div>
            </div>
          )}

          {/* Main content grid */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 16, marginBottom: 16 }}>
            <div>
              <GovCostAnalysisItemsTable
                versionId={fullVersion.id}
                items={items}
                sources={sources}
                itemSources={itemSources}
                readOnly={isFinal}
                onChanged={() => selectedVersionId && fetchVersion(selectedVersionId)}
              />
            </div>
            <div>
              <GovCostAnalysisSidebar
                version={fullVersion}
                readOnly={isFinal}
                onChanged={() => selectedVersionId && fetchVersion(selectedVersionId)}
              />
            </div>
          </div>

          <GovCostAnalysisRisksAssumptions
            versionId={fullVersion.id}
            risks={risks}
            assumptions={assumptions}
            readOnly={isFinal}
            onChanged={() => selectedVersionId && fetchVersion(selectedVersionId)}
          />

          {/* Actuals panel — only shows if there's a linked construction job */}
          {isConstruction && constructionJobId && (
            <GovCostAnalysisActualsPanel
              versionId={fullVersion.id}
              constructionJobId={constructionJobId}
              expected={fullVersion.total_expected}
              low={fullVersion.total_low}
              high={fullVersion.total_high}
            />
          )}
        </>
      )}

      {showRunModal && renderRunModal()}
      {showExport && fullVersion && (
        <GovCostAnalysisExportModal
          versionId={fullVersion.id}
          onClose={() => setShowExport(false)}
        />
      )}
    </div>
  );

  function renderRunModal() {
    return (
      <div style={S.modalBackdrop} onClick={() => setShowRunModal(false)}>
        <div style={{ ...S.modal, width: 620 }} onClick={e => e.stopPropagation()}>
          <h2 style={S.modalTitle}>🤖 Generate AI Cost Analysis</h2>
          <p style={{ color: GOV_COLORS.textMuted, fontSize: 13, marginBottom: 20 }}>
            Claude will read the selected documents and propose scope, line items, risks, and assumptions.
            You'll be able to edit everything before finalizing.
          </p>

          <div style={S.modalRow}>
            <label style={S.modalLabel}>Documents to Analyze ({selectedDocIds.length}/{documents.length} selected)</label>
            <div style={{ maxHeight: 260, overflowY: 'auto', border: `1px solid ${GOV_COLORS.border}`, borderRadius: 8, padding: 8 }}>
              {documents.length === 0 ? (
                <div style={{ color: GOV_COLORS.textDim, fontSize: 12, padding: 16, textAlign: 'center' }}>
                  No documents attached. Upload documents in the Documents tab first.
                </div>
              ) : (
                (() => {
                  const nativeExts = ['.pdf', '.txt', '.md', '.csv', '.html', '.json', '.xml'];
                  const nativeMimes = ['application/pdf', 'text/plain', 'text/markdown', 'text/csv', 'text/html', 'application/json', 'application/xml'];
                  const convertibleExts = ['.xlsx', '.xls', '.xlsm', '.docx', '.tsv'];
                  // Token estimator with empirical bytes-per-token ratios per format.
                  // Must match backend/services/govCostAnalysis.js::estimateTokens.
                  const CTX_BUDGET = 800_000;
                  const est = (d: any) => {
                    const bytes = d.byte_size || d.file_size_bytes || 0;
                    const s = (d.mime_type || d.file_name || '').toLowerCase();
                    if (/pdf/.test(s)) return Math.ceil(bytes / 15);
                    if (/docx|word/.test(s)) return Math.ceil(bytes / 40);
                    if (/xlsx|excel|sheet/.test(s)) return Math.ceil(bytes / 30);
                    if (/tsv|csv|text|json|xml|markdown|html/.test(s)) return Math.ceil(bytes / 4);
                    return Math.ceil(bytes / 15);
                  };
                  const selectedTokens = documents
                    .filter(d => selectedDocIds.includes(d.id))
                    .reduce((s, d) => s + est(d), 0);
                  const overBudget = selectedTokens > CTX_BUDGET;

                  return (
                    <>
                      <div style={{
                        padding: '6px 10px', marginBottom: 8, borderRadius: 6,
                        background: overBudget ? '#3d1414' : '#0F1923',
                        border: `1px solid ${overBudget ? GOV_COLORS.danger : GOV_COLORS.border}`,
                        color: overBudget ? GOV_COLORS.danger : GOV_COLORS.textMuted,
                        fontSize: 11, fontWeight: overBudget ? 700 : 400,
                      }}>
                        {overBudget ? '⚠️ ' : '📊 '}
                        Est. context: {(selectedTokens / 1000).toFixed(0)}K / 800K tokens
                        {overBudget && ` — backend will auto-drop lowest-priority docs (regulations first)`}
                      </div>
                      {documents.map(d => {
                        const name = (d.file_name || d.name || '').toLowerCase();
                        const mime = (d.mime_type || '').toLowerCase();
                        const isNative = nativeMimes.includes(mime) || nativeExts.some(ext => name.endsWith(ext));
                        const isConvertible = convertibleExts.some(ext => name.endsWith(ext));
                        const readable = isNative || isConvertible;
                        const tokens = est(d);
                        const isBig = tokens > 100_000;
                        return (
                          <label key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 8px', cursor: readable ? 'pointer' : 'not-allowed', borderRadius: 4, opacity: readable ? 1 : 0.5 }}>
                            <input type="checkbox"
                              disabled={!readable}
                              checked={readable && selectedDocIds.includes(d.id)}
                              onChange={e => setSelectedDocIds(prev =>
                                e.target.checked ? [...prev, d.id] : prev.filter(id => id !== d.id))}
                            />
                            <div style={{ flex: 1, fontSize: 13, color: GOV_COLORS.text }}>
                              {d.file_name || d.name}
                              <span style={{ color: GOV_COLORS.textDim, marginLeft: 8, fontSize: 11 }}>
                                {d.category ? `· ${d.category}` : ''}
                                {d.byte_size || d.file_size_bytes ? ` · ${((d.byte_size || d.file_size_bytes) / 1024 / 1024).toFixed(1)}MB` : ''}
                                {readable && (
                                  <span style={{ color: isBig ? GOV_COLORS.warning : GOV_COLORS.textDim, marginLeft: 4 }}>
                                    · ~{tokens >= 1000 ? `${(tokens / 1000).toFixed(0)}K` : tokens} tok
                                  </span>
                                )}
                                {isConvertible && ' · 🔄 converts to text'}
                                {!readable && ` · ⚠️ ${mime || 'unknown'} unsupported`}
                              </span>
                            </div>
                          </label>
                        );
                      })}
                    </>
                  );
                })()
              )}
            </div>
            <div style={{ color: GOV_COLORS.textMuted, fontSize: 11, marginTop: 6 }}>
              💡 Tip: exclude large drawings you don't need for cost estimation — every file adds token cost.
            </div>
          </div>

          <div style={{ ...S.card, marginBottom: 16, padding: 12, background: '#0F1923' }}>
            <div style={{ fontSize: 12, color: GOV_COLORS.textMuted }}>
              💰 Budget cap: $15.00 per run (adjustable in Render env)<br />
              ⏱️ Typical runtime: 2-5 minutes<br />
              🎯 Model: Claude Opus 4-7 with citations enabled
            </div>
          </div>

          <div style={S.modalFooter}>
            <button onClick={() => setShowRunModal(false)} style={{ ...S.cancelBtn, flex: 1 }}>Cancel</button>
            <button onClick={() => queueRun('generate')} disabled={queueing || selectedDocIds.length === 0}
              style={{ ...S.addBtn, flex: 2, opacity: queueing ? 0.6 : 1 }}>
              {queueing ? 'Queueing…' : '🚀 Start Analysis'}
            </button>
          </div>
        </div>
      </div>
    );
  }
}

function KPI({ label, value, sub, color }: { label: string; value: string; sub?: string; color: string }) {
  return (
    <div style={{ ...S.card, borderLeft: `4px solid ${color}` }}>
      <div style={{ color: GOV_COLORS.textMuted, fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase' }}>{label}</div>
      <div style={{ color: '#fff', fontSize: 24, fontWeight: 800, marginTop: 6 }}>{value}</div>
      {sub && <div style={{ color: GOV_COLORS.textMuted, fontSize: 11, marginTop: 4 }}>{sub}</div>}
    </div>
  );
}
